import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { KeyTerm, ImportantNote, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const microeconomicsUnit1Notes: UnitNotes = {
  unitId: "unit-1",
  unitName: "Unit 1: Basic Economic Concepts",
  examWeight: "12–15%",
  focus: "The fundamental economic problem of scarcity and the models used to understand choice, trade, and consumer behavior.",
  sections: [
    {
      id: "topic-1-1",
      title: "Topic 1.1: Scarcity",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 The Fundamental Economic Problem</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              <strong>Economics</strong> is the social science concerned with the efficient use of scarce resources to achieve the maximum satisfaction of economic wants.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <KeyTerm 
                term="Scarcity" 
                definition="A permanent condition existing because society has unlimited wants but limited resources (e.g., time, gold)."
              />
              <KeyTerm 
                term="Shortage" 
                definition="A temporary market condition where Quantity Demanded > Quantity Supplied, usually due to price controls."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛠️ The Factors of Production (Resources)</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">🏞️ Land</h4>
                <p className="text-sm text-slate-700 mt-1">All natural resources (e.g., oil, water, timber, minerals).</p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">👥 Labor</h4>
                <p className="text-sm text-slate-700 mt-1">Physical and mental effort exerted by humans in production.</p>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🏗️ Capital</h4>
                <div className="space-y-2 mt-1">
                  <p className="text-xs text-slate-700"><strong>Physical Capital:</strong> Tools, machinery, and technology. (Note: Money is NOT capital).</p>
                  <p className="text-xs text-slate-700"><strong>Human Capital:</strong> Skills and knowledge gained through education/training.</p>
                </div>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-fuchsia-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">🚀 Entrepreneurship</h4>
                <p className="text-sm text-slate-700 mt-1">Individuals who combine the other three factors to create goods/services and take risks to earn profits.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-2",
      title: "Topic 1.2: Resource Allocation and Economic Systems",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">❓ The Three Basic Economic Questions</h3>
            <p className="text-slate-700 mb-4 font-medium italic">Because of scarcity, every society must answer:</p>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700">
              <li><strong>What</strong> goods and services should be produced?</li>
              <li><strong>How</strong> should these goods and services be produced?</li>
              <li><strong>For whom</strong> should these goods and services be produced?</li>
            </ol>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ Economic Systems</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-400">
                <h4 className="font-bold text-red-900">🚩 Command Economy (Centrally Planned)</h4>
                <p className="text-sm text-slate-700 mt-1">Government owns resources and directs production. Characterized by little private property, a focus on equity, and frequent inefficiency due to lack of competition.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-900">🤝 Market Economy (Laissez-Faire)</h4>
                <p className="text-sm text-slate-700 mt-1">Private individuals own resources. Regulated by the "Invisible Hand" (Supply & Demand). Driven by profit motives, competition, and consumer sovereignty.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-400">
                <h4 className="font-bold text-slate-900">🔄 Mixed Economy</h4>
                <p className="text-sm text-slate-700 mt-1">A blend of command and market. Government regulates to correct market failures while markets allocate most resources (e.g., US, China).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-3",
      title: "Topic 1.3: Production Possibilities Curve (PPC)",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📈 Definitions and Assumptions</h3>
            <p className="text-slate-700 mb-4">Shows the maximum combinations of two goods an economy can produce.</p>
            <ul className="list-disc pl-6 space-y-1 text-sm text-slate-600 mb-4">
              <li>Assumptions: Full employment, fixed resources, fixed technology, two-good economy.</li>
            </ul>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="p-3 border border-slate-200 rounded-lg bg-emerald-50">
                <h5 className="font-bold text-emerald-900 text-xs uppercase">Points ON Curve</h5>
                <p className="text-xs text-slate-700 mt-1">Productive Efficiency. Resources fully utilized.</p>
              </div>
              <div className="p-3 border border-slate-200 rounded-lg bg-amber-50">
                <h5 className="font-bold text-amber-900 text-xs uppercase">Points INSIDE Curve</h5>
                <p className="text-xs text-slate-700 mt-1">Inefficiency. Recession or unemployment.</p>
              </div>
              <div className="p-3 border border-slate-200 rounded-lg bg-red-50">
                <h5 className="font-bold text-red-900 text-xs uppercase">Points OUTSIDE Curve</h5>
                <p className="text-xs text-slate-700 mt-1">Impossible/Unattainable given current resources.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔄 Opportunity Cost & Shape</h3>
            <div className="space-y-3">
              <div className="p-4 rounded-lg border border-slate-200 bg-white">
                <h4 className="font-bold text-slate-900">📏 Straight Line PPC</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>Constant Opportunity Cost:</strong> Resources are easily adaptable between goods (e.g., Pizza vs. Calzones).</p>
              </div>
              <div className="p-4 rounded-lg border border-slate-200 bg-white">
                <h4 className="font-bold text-slate-900">🏹 Bowed Out (Concave) PPC</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>Increasing Opportunity Cost:</strong> Resources are specialized. As you produce more of Good A, you give up increasingly more of Good B. This is the <strong>Law of Increasing Opportunity Cost</strong>.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🚀 Shifting the PPC (Economic Growth)</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-900 mb-2">Causes of an Outward Shift:</h4>
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">
                <li>Increase in <strong>Quantity</strong> of resources (e.g., new land, migration).</li>
                <li>Increase in <strong>Quality</strong> of resources (e.g., better human capital).</li>
                <li>Advances in <strong>Technology</strong>.</li>
              </ul>
            </div>
            <ImportantNote>
              A decrease in unemployment moves a point from <strong>inside</strong> to <strong>on</strong> the curve; it does <strong>not</strong> shift the curve itself.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-4",
      title: "Topic 1.4: Comparative Advantage and Trade",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚔️ Absolute vs. Comparative Advantage</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <KeyTerm 
                term="Absolute Advantage" 
                definition="Producing more output with the same inputs, or using fewer inputs than another producer."
              />
              <KeyTerm 
                term="Comparative Advantage" 
                definition="Producing a good at a lower opportunity cost. A country cannot have the comparative advantage in both goods."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔢 Calculating Opportunity Cost</h3>
            <div className="space-y-4">
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <h4 className="font-bold text-indigo-900">Output Problems (Quantity Produced)</h4>
                <Mnemonic title="OOO" content="Other Over. (Cost of A = B / A)" />
                <div className="mt-2 text-sm text-slate-700 italic">Example: USA makes 20 planes or 40 cars.</div>
                <BlockMath math="\text{Cost of 1 Plane} = \frac{40}{20} = 2 \text{ Cars}" />
              </div>

              <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                <h4 className="font-bold text-emerald-900">Input Problems (Hours/Resources used)</h4>
                <Mnemonic title="IOU" content="It Over (or Other Under). (Cost of A = A / B)" />
                <div className="mt-2 text-sm text-slate-700 italic">Example: Takes 5 hours for a Pie, 10 hours for a Cake.</div>
                <BlockMath math="\text{Cost of 1 Pie} = \frac{5}{10} = 0.5 \text{ Cakes}" />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🤝 Terms of Trade</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Trade allows consumption <strong>outside</strong> the PPC. Beneficial terms must fall <strong>between</strong> the opportunity costs of the two producers.
            </p>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-5",
      title: "Topic 1.5: Cost-Benefit Analysis",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧠 Rational Expectations</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Rational agents take an action if and only if:
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 text-center">
              <BlockMath math="\text{Marginal Benefit (MB)} \geq \text{Marginal Cost (MC)}" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💰 Types of Costs</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Explicit Costs</h4>
                <p className="text-sm text-slate-700">Out-of-pocket monetary payments (tuition, wages).</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">Implicit Costs</h4>
                <p className="text-sm text-slate-700">Opportunity cost of self-owned resources (forgone salary).</p>
              </div>
              <div className="bg-slate-800 text-white p-4 rounded-lg shadow-lg">
                <h4 className="font-bold text-slate-200">Total Economic Cost</h4>
                <div className="mt-2">
                  <BlockMath math="\text{Explicit Costs} + \text{Implicit Costs}" />
                </div>
              </div>
            </div>
            <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
              <h4 className="font-bold text-green-900">Net Benefit</h4>
              <BlockMath math="\text{Net Benefit} = \text{Total Benefit} - \text{Total Cost}" />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-6",
      title: "Topic 1.6: Marginal Analysis and Consumer Choice",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🍕 Utility Theory</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Total Utility</h4>
                <p className="text-sm text-slate-700">Cumulative satisfaction from consumption.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Marginal Utility (MU)</h4>
                <p className="text-sm text-slate-700">Additional satisfaction from one more unit.</p>
                <BlockMath math="MU = \frac{\Delta \text{Total Utility}}{\Delta \text{Quantity}}" />
              </div>
            </div>
            <KeyTerm 
              term="Law of Diminishing Marginal Utility" 
              definition="As you consume more units of a specific good, the additional satisfaction from each subsequent unit eventually declines."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ The Utility Maximization Rule</h3>
            <p className="text-slate-700 mb-4">To maximize utility within a budget, allocate spending where:</p>
            <div className="bg-blue-600 text-white p-6 rounded-lg shadow-inner text-center">
              <BlockMath math="\frac{MU_x}{P_x} = \frac{MU_y}{P_y}" />
            </div>
            <div className="grid md:grid-cols-2 gap-3 mt-4">
              <div className="bg-red-50 p-3 rounded border border-red-200">
                <p className="text-xs text-slate-700">If <InlineMath math="\frac{MU_x}{P_x} > \frac{MU_y}{P_y}" />:</p>
                <p className="font-bold text-red-900 text-sm">Buy MORE X, LESS Y</p>
              </div>
              <div className="bg-green-50 p-3 rounded border border-green-200">
                <p className="text-xs text-slate-700">If <InlineMath math="\frac{MU_x}{P_x} < \frac{MU_y}{P_y}" />:</p>
                <p className="font-bold text-green-900 text-sm">Buy LESS X, MORE Y</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "unit-1-summary",
      title: "Unit 1 Summary & Exam Prep",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📋 Key Formulas Checklist</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <ul className="space-y-3">
                <li className="flex justify-between items-center text-sm border-b pb-1">
                  <span>Opp. Cost (Output)</span>
                  <InlineMath math="\frac{\text{Other}}{\text{Given}}" />
                </li>
                <li className="flex justify-between items-center text-sm border-b pb-1">
                  <span>Opp. Cost (Input)</span>
                  <InlineMath math="\frac{\text{Given}}{\text{Other}}" />
                </li>
                <li className="flex justify-between items-center text-sm border-b pb-1">
                  <span>Marginal Benefit</span>
                  <InlineMath math="\Delta \text{TB} / \Delta \text{Q}" />
                </li>
                <li className="flex justify-between items-center text-sm border-b pb-1">
                  <span>Marginal Utility</span>
                  <InlineMath math="\Delta \text{TU} / \Delta \text{Q}" />
                </li>
                <li className="flex justify-between items-center text-sm">
                  <span>Utility Maximization</span>
                  <InlineMath math="MU_x/P_x = MU_y/P_y" />
                </li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚠️ Common Pitfalls & Exam Tips</h3>
            <div className="space-y-3">
              <div className="bg-amber-50 p-4 rounded-lg border-l-4 border-amber-400">
                <p className="text-sm text-slate-800"><strong>Price vs. Cost:</strong> Price is what you pay; Cost is opportunity cost. Don't confuse them!</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                <p className="text-sm text-slate-800"><strong>Efficiency:</strong> Productive efficiency is any point ON the PPC. Allocative efficiency is the specific point society wants most.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-400">
                <p className="text-sm text-slate-800"><strong>Sunk Costs:</strong> Costs already paid and unrecoverable. <strong>Ignore them</strong> in marginal decision-making.</p>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg border-l-4 border-indigo-400">
                <p className="text-sm text-slate-800"><strong>PPC Axis Labels:</strong> Always label axes (e.g., "Consumer Goods"). Never leave them blank.</p>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default microeconomicsUnit1Notes;